import React from "react";
import { Link } from "react-router-dom";

/**
 * NAGUARÀ pack — ONE single brown bag image for every coffee.
 * Fixed layers: bag + official logo + printed "250 g" (the src never changes).
 * Variable layer: the sticker (colour + data). Nothing else moves.
 */
const PACK = "/products/naguara/pack-base-fondoplano.webp";
const PACK_SM = "/products/naguara/pack-base-fondoplano-392.webp";
const LOGO = "/brand/naguara/naguara-logo-completo-amarillo-original.svg";
const INK = "#23262F";
/* Flat-bottom pack (box pouch): bag 784x956 (0.82) — sticker 1200x1300 (0.92).
   Closure at the top border (4.6%-12.4%), logo at 22.1% so there is a clean
   9.7% band of brown between seal and wordmark, side gusset fold confined to
   the bottom right corner (87.8%-100%). */
const STICKER_POS = "absolute left-[21.5%] w-[57%] top-[38%]";

const kSize = (s) => (s.length > 12 ? 33 : s.length > 9 ? 36 : 40);
const vSize = (s) => (s.length > 24 ? 32 : s.length > 19 ? 36 : 39);
const ROWS_TOP = 520;
const ROWS_BOTTOM = 1000;

export const Sticker = ({ product, className = "", slot }) => {
  const l = product.label;
  // Origins without an approved spec sheet print only the origin + "café de
  // especialidad": no species, no rows, no tasting notes, no empty frames.
  const minimal = !l.rows.length && !l.notas.length;
  return (
    <svg
      viewBox="0 0 1200 1300"
      className={className}
      data-slot={slot}
      data-testid={`pack-sticker-${product.id}`}
    >
      <defs>
        <linearGradient id={`sh-${product.id}`} x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stopColor="#000" stopOpacity="0.15" />
          <stop offset="0.14" stopColor="#000" stopOpacity="0" />
          <stop offset="0.86" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" stopOpacity="0.12" />
        </linearGradient>
      </defs>

      <rect width="1200" height="1300" rx="16" fill={product.sticker} stroke="rgba(0,0,0,0.14)" strokeWidth="3" />

      {minimal ? (
        <g fill={INK} fontFamily='"Outfit", sans-serif' textAnchor="middle" dominantBaseline="central">
          <text x="600" y="112" fontSize="42" fontWeight="600" letterSpacing="3">
            CAFÉ DE ESPECIALIDAD EN GRANO
          </text>
          <text x="600" y="192" fontSize="42" fontWeight="600" letterSpacing="3">
            100% ARÁBICA
          </text>
          <text
            x="600"
            y="760"
            fontSize="122"
            letterSpacing="6"
            fontFamily='"Playfair Display", Georgia, serif'
          >
            {product.origin.toUpperCase()}
          </text>
        </g>
      ) : (
        <>
          <g fill={INK} fontFamily='"Outfit", sans-serif' textAnchor="middle" dominantBaseline="central">
            <text x="600" y="112" fontSize="42" fontWeight="600" letterSpacing="3">
              CAFÉ DE ESPECIALIDAD EN GRANO
            </text>
            <text x="600" y="192" fontSize="42" fontWeight="600" letterSpacing="3">
              100% ARÁBICA
            </text>
            <text
              x="600"
              y="322"
              fontSize="122"
              letterSpacing="6"
              fontFamily='"Playfair Display", Georgia, serif'
            >
              {product.origin.toUpperCase()}
            </text>
            <text x="600" y="446" fontSize={l.sub.length > 22 ? 34 : 40} fontWeight="600" letterSpacing="5">
              {l.sub}
            </text>
            <text x="600" y="1158" fontSize="41" fontWeight="500" letterSpacing="1.5">
              {l.notas[0]}
            </text>
            <text x="600" y="1216" fontSize="41" fontWeight="500" letterSpacing="1.5">
              {l.notas[1]}
            </text>
          </g>

          <g stroke={INK} strokeWidth="2" opacity="0.9">
            <line x1="316" y1="446" x2="360" y2="446" />
            <line x1="840" y1="446" x2="884" y2="446" />
          </g>

          <g fill={INK} fontFamily='"Outfit", sans-serif' dominantBaseline="central">
            {l.rows.map(([k, v], i) => {
              const slotH = (ROWS_BOTTOM - ROWS_TOP) / l.rows.length;
              const y = ROWS_TOP + slotH * i + slotH * 0.45;
              return (
                <g key={k}>
                  <text x="130" y={y} fontSize={kSize(k)} fontWeight="600" letterSpacing="2">
                    {k}
                  </text>
                  <text x="506" y={y} fontSize={vSize(v)} fontWeight="500" letterSpacing="1.5">
                    {v}
                  </text>
                  {k === "TUESTE" && l.dots
                    ? [0, 1, 2, 3, 4].map((j) => (
                        <circle
                          key={j}
                          cx={773 + j * 67}
                          cy={y}
                          r="16"
                          fill={j < l.dots ? INK : "none"}
                          stroke={INK}
                          strokeWidth="3"
                        />
                      ))
                    : null}
                  <line
                    x1="114"
                    y1={ROWS_TOP + slotH * (i + 1)}
                    x2="1110"
                    y2={ROWS_TOP + slotH * (i + 1)}
                    stroke={INK}
                    strokeWidth="2"
                  />
                </g>
              );
            })}
            <rect x="114" y="1030" width="1002" height="230" rx="14" fill="none" stroke={INK} strokeWidth="3" />
            <text x="168" y="1090" fontSize="36" fontWeight="600" letterSpacing="2">
              NOTAS DE CATA:
            </text>
          </g>
        </>
      )}

      <rect width="1200" height="1300" rx="16" fill={`url(#sh-${product.id})`} />
    </svg>
  );
};

/** Fixed layers — identical for every coffee, never re-mounted. */
const PackBase = ({ shadow = "", priority = false, sizes = "(max-width: 768px) 60vw, 30vw" }) => (
  <>
    <img
      src={PACK}
      srcSet={`${PACK_SM} 392w, ${PACK} 784w`}
      sizes={sizes}
      alt=""
      aria-hidden="true"
      draggable="false"
      decoding="async"
      {...(priority ? { fetchPriority: "high" } : { loading: "lazy" })}
      className={`absolute inset-0 w-full h-full object-contain select-none transition-none ${shadow}`}
    />
    <img
      src={LOGO}
      alt="Naguarà"
      draggable="false"
      decoding="async"
      {...(priority ? { fetchPriority: "high" } : { loading: "lazy" })}
      className="absolute left-[23%] w-[54%] top-[22.1%] select-none transition-none"
    />
    <svg viewBox="0 0 200 60" className="absolute right-[13%] bottom-[4%] w-[20%]" aria-hidden="true">
      <text
        x="200"
        y="30"
        textAnchor="end"
        dominantBaseline="central"
        fontSize="44"
        fontWeight="300"
        fontFamily='"Outfit", sans-serif'
        fill="#EFD9A0"
      >
        250 g
      </text>
    </svg>
  </>
);

/** Single coffee (product cards, cart). */
export const BagPack = ({ product, className = "", mini = false, testId, shadow = "", priority = false, alt }) => (
  <div
    role="img"
    aria-label={alt || `${product.name || "Naguarà"} · 250 g`}
    className={`relative transition-none ${className}`}
    style={{ aspectRatio: "784 / 956" }}
    data-testid={testId || `pack-${product.id || "base"}`}
  >
    <PackBase shadow={shadow} priority={priority} />
    {mini ? (
      <div
        className="absolute left-[21.5%] w-[57%] top-[38%] h-[50%] rounded-[3px]"
        style={{ backgroundColor: product.sticker }}
      />
    ) : (
      <Sticker product={product} className={STICKER_POS} />
    )}
  </div>
);

/**
 * Hero pack. product-stage = fixed reserved space (never reflows).
 * product-float = single permanent node carrying the ambient float; it is never
 * remounted, so swapping the sticker cannot restart or shift the animation.
 *
 * Two sticker slots (double buffer): the incoming label is written into the
 * hidden slot, so it is already rasterised when its 150 ms fade-in starts. The
 * fade stays strictly sequential (out 150 ms -> in 150 ms), never a cross-fade.
 */
export const PackSwap = ({
  slots,
  active,
  phase,
  stageClass = "",
  shadow = "",
  testId,
  href,
  ariaLabel,
  priority = false,
}) => {
  const layers = (
    <>
      <PackBase shadow={shadow} priority={priority} sizes="(max-width: 768px) 85vw, 45vw" />
      {slots.map((p, i) =>
        p ? (
          <Sticker
            key={i}
            slot={i}
            product={p}
            className={`${STICKER_POS} pack-fade ${
              i === active && phase !== "out" ? "opacity-100" : "opacity-0"
            }`}
          />
        ) : null
      )}
    </>
  );

  return (
    <div className={`product-stage ${stageClass}`} data-testid={testId}>
      <div className="product-float" data-testid={`${testId}-float`}>
        {href ? (
          /* The whole visible pack is one accessible link. It lives INSIDE the
             floating node, so the hit area matches the bag at every instant and
             changing its target never restarts the animation. */
          <Link
            to={href}
            aria-label={ariaLabel}
            data-testid={`${testId}-link`}
            className="absolute inset-0 block cursor-pointer pointer-events-auto rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-[#F4D35E] focus-visible:ring-offset-4 focus-visible:ring-offset-transparent"
          >
            {layers}
          </Link>
        ) : (
          layers
        )}
      </div>
    </div>
  );
};

export default BagPack;
